import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiUsers } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getParentMenuItems } from '../../components/layout/ParentMenuConfig';
import EducationalParticles from '../../components/common/EducationalParticles';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  parentStudentAPI,
  parentGradeAPI,
} from '../../api/parentEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  ContentGrid,
  ContentCard,
  ContentCardHeader,
  ContentCardBody,
  ContentCardFooter,
  ContentActions,
  ActionButton,
  EmptyState,
} from '../dashboards/Dashboard.styles';

const StudentsPage = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [showRemoveStudentModal, setShowRemoveStudentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [studentForm, setStudentForm] = useState({
    username: '',
    password: '',
    grade_id: '',
  });

  // Use centralized menu configuration
  const menuItems = getParentMenuItems();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [studentsRes, gradesRes] = await Promise.all([
        parentStudentAPI.getStudents(),
        parentGradeAPI.getGrades(),
      ]);
      
      setStudents(studentsRes.data.students || []);
      setGrades(gradesRes.data.grades || []);
    } catch (error) {
      showToast('error', 'Failed to load students data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await parentStudentAPI.addStudent(studentForm);
      showToast('success', 'Student added successfully!');
      setShowAddStudentModal(false);
      setStudentForm({
        username: '',
        password: '',
        grade_id: '',
      });
      fetchData();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to add student');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    
    setSubmitting(true);
    try {
      const updateData = {
        username: studentForm.username,
        grade_id: studentForm.grade_id,
      };
      
      if (studentForm.password.trim()) {
        updateData.password = studentForm.password;
      }
      
      await parentStudentAPI.updateStudent(selectedStudent.id, updateData);
      showToast('success', 'Student updated successfully!');
      setShowEditStudentModal(false);
      setSelectedStudent(null);
      fetchData();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to update student');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveStudent = async () => {
    if (!selectedStudent) return;
    
    try {
      await parentStudentAPI.removeStudent(selectedStudent.id);
      showToast('success', 'Student removed successfully');
      setShowRemoveStudentModal(false);
      setSelectedStudent(null);
      fetchData();
    } catch (error) {
      showToast('error', 'Failed to remove student');
    }
  };

  const openEditStudentModal = (student) => {
    setSelectedStudent(student);
    setStudentForm({
      username: student.username,
      password: '',
      grade_id: student.grade_id || '',
    });
    setShowEditStudentModal(true);
  };

  const openRemoveStudentModal = (student) => {
    setSelectedStudent(student);
    setShowRemoveStudentModal(true);
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading students..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <DashboardContainer>
      <DashboardHeader>
        <div>
          <h1>Student Management</h1>
          <p>Add and manage your students</p>
        </div>
        <Button leftIcon={<FiPlus />} onClick={() => setShowAddStudentModal(true)}>
          Add Student
        </Button>
      </DashboardHeader>

      {students.length === 0 ? (
        <EmptyState>
          <FiUsers size={48} />
          <h3>No students added</h3>
          <p>Add your first student to get started</p>
          <Button leftIcon={<FiPlus />} onClick={() => setShowAddStudentModal(true)}>
            Add Student
          </Button>
        </EmptyState>
      ) : (
        <ContentGrid>
          {students.map((student) => (
            <ContentCard key={student.id}>
              <ContentCardHeader>
                <div>
                  <h3>{student.username}</h3>
                  <span 
                    className="badge"
                    style={{
                      background: student.is_primary ? '#10B98115' : '#2c5f3415',
                      color: student.is_primary ? '#10B981' : '#2c5f34',
                    }}
                  >
                    {student.is_primary ? 'Primary' : 'Secondary'}
                  </span>
                </div>
              </ContentCardHeader>
              <ContentCardBody>
                <div className="meta" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                  <span>📧 {student.email}</span>
                  <span>📚 {student.grade || 'No grade assigned'}</span>
                  <span>🔗 {student.relationship}</span>
                </div>
              </ContentCardBody>
              <ContentCardFooter>
                <ContentActions>
                  <ActionButton title="Edit" onClick={() => openEditStudentModal(student)}>
                    <FiEdit />
                  </ActionButton>
                  <ActionButton title="Remove" onClick={() => openRemoveStudentModal(student)}>
                    <FiTrash2 />
                  </ActionButton>
                </ContentActions>
              </ContentCardFooter>
            </ContentCard>
          ))}
        </ContentGrid>
      )}

      {/* Add Student Modal */}
      <Modal
        isOpen={showAddStudentModal}
        onClose={() => setShowAddStudentModal(false)}
        title="Add New Student"
        size="medium"
      >
        <form onSubmit={handleAddStudent}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Input
              label="Username"
              type="text"
              value={studentForm.username}
              onChange={(e) => setStudentForm({ ...studentForm, username: e.target.value })}
              placeholder="Enter username for the student"
              required
            />
            
            <Input
              label="Password"
              type="password"
              value={studentForm.password}
              onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
              placeholder="Enter password for the student"
              required
            />
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Grade
              </label>
              <select
                value={studentForm.grade_id}
                onChange={(e) => setStudentForm({ ...studentForm, grade_id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px',
                  fontSize: '16px',
                }}
                required
              >
                <option value="">Select Grade</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </select>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px', 
              marginTop: '8px',
              flexWrap: 'wrap'
            }}>
              <Button variant="ghost" onClick={() => setShowAddStudentModal(false)} type="button">
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                Add Student
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Edit Student Modal */}
      <Modal
        isOpen={showEditStudentModal}
        onClose={() => setShowEditStudentModal(false)}
        title="Edit Student"
        size="medium"
      >
        <form onSubmit={handleUpdateStudent}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Input
              label="Username"
              type="text"
              value={studentForm.username}
              onChange={(e) => setStudentForm({ ...studentForm, username: e.target.value })}
              placeholder="Enter username for the student"
              required
            />
            
            <Input
              label="Password"
              type="password"
              value={studentForm.password}
              onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
              placeholder="Enter new password (leave blank to keep current)"
            />
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Grade
              </label>
              <select
                value={studentForm.grade_id}
                onChange={(e) => setStudentForm({ ...studentForm, grade_id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px',
                  fontSize: '16px',
                }}
                required
              >
                <option value="">Select Grade</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </select>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px', 
              marginTop: '8px',
              flexWrap: 'wrap'
            }}>
              <Button variant="ghost" onClick={() => setShowEditStudentModal(false)} type="button">
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                Update Student
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Remove Student Modal */}
      <Modal
        isOpen={showRemoveStudentModal}
        onClose={() => setShowRemoveStudentModal(false)}
        title="Remove Student"
        size="small"
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ margin: '0 0 20px 0', color: theme.colors.textSecondary }}>
            Are you sure you want to remove this student from your account? This action cannot be undone.
          </p>
          {selectedStudent && (
            <div style={{ 
              background: theme.colors.backgroundSecondary, 
              padding: '16px', 
              borderRadius: '8px', 
              marginBottom: '20px',
            }}>
              <h4 style={{ margin: '0 0 8px 0' }}>
                {selectedStudent.username}
              </h4>
              <p style={{ margin: 0, fontSize: '14px', color: theme.colors.textSecondary }}>
                {selectedStudent.email}
              </p>
            </div>
          )}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <Button 
              variant="ghost" 
              onClick={() => setShowRemoveStudentModal(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRemoveStudent}
              style={{ background: '#EF4444' }}
            >
              Remove Student
            </Button>
          </div>
        </div>
      </Modal>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default StudentsPage;
